// How a settled invitation READS (arc 101 slice 01). `lib/invitations.ts` owns
// the POST and the verdict; this owns the one thing that changes for a different
// reason — the sentence each verdict is framed with. Same split as
// `lib/task-dispatch.ts` / `lib/task-refusal.ts`, for the same reason.
//
// Pure: no DOM, no query, no router. Every screen an invitee can land on is
// decidable from an `InviteOutcome` alone, so it is testable without either.

import type { InviteOutcome } from './invitations';

/**
 * One screen per thing worth saying. It is {@link InviteOutcome}'s `kind` with
 * `already_accepted` SPLIT, because that verdict carries `bySelf` and the two
 * sides of it are not the same news: a redelivered click is a no-op to ignore,
 * and someone else holding your link is a security event to report. The API
 * ships that bit precisely so a client can say which; one screen for both would
 * throw it away at the last hop.
 */
export type InviteScreen =
  | 'accepted'
  | 'already_a_member'
  | 'already_accepted'
  | 'accepted_by_other'
  | 'pending_funding'
  | 'expired'
  | 'revoked'
  | 'not_live'
  | 'not_found'
  | 'no_token'
  | 'failed';

/** Which screen an outcome lands on. */
export function screenFor(outcome: InviteOutcome): InviteScreen {
  if (outcome.kind === 'already_accepted') {
    return outcome.bySelf ? 'already_accepted' : 'accepted_by_other';
  }
  return outcome.kind;
}

/**
 * Whether a screen tells someone they got in, that they did not, or that they
 * are recorded and waiting (`pending`: the next screen is the waitlist wall).
 */
export type InviteTone = 'ok' | 'refusal' | 'pending';

/** Title + body + tone, as catalog keys. See `locales/en.json` § `invite`. */
export type InviteCopy = {
  readonly title: string;
  readonly body: string;
  readonly tone: InviteTone;
};

/**
 * The copy, keyed by screen. `satisfies Record<InviteScreen, …>` is the point: a
 * verdict added to the API and threaded through `InviteOutcome` without a
 * sentence for the person reading it is a compile error, not a screen rendering
 * the translator's `⟦key⟧` placeholder at a stranger's first contact with the
 * product.
 */
export const INVITE_COPY = {
  accepted: { title: 'invite.accepted_title', body: 'invite.accepted_body', tone: 'ok' },
  already_a_member: {
    title: 'invite.already_a_member_title',
    body: 'invite.already_a_member_body',
    tone: 'ok',
  },
  already_accepted: {
    title: 'invite.already_accepted_title',
    body: 'invite.already_accepted_body',
    tone: 'ok',
  },
  accepted_by_other: {
    title: 'invite.accepted_by_other_title',
    body: 'invite.accepted_by_other_body',
    tone: 'refusal',
  },
  pending_funding: {
    title: 'invite.pending_funding_title',
    body: 'invite.pending_funding_body',
    tone: 'pending',
  },
  expired: { title: 'invite.expired_title', body: 'invite.expired_body', tone: 'refusal' },
  revoked: { title: 'invite.revoked_title', body: 'invite.revoked_body', tone: 'refusal' },
  not_live: { title: 'invite.not_live_title', body: 'invite.not_live_body', tone: 'refusal' },
  not_found: { title: 'invite.not_found_title', body: 'invite.not_found_body', tone: 'refusal' },
  no_token: { title: 'invite.no_token_title', body: 'invite.no_token_body', tone: 'refusal' },
  failed: { title: 'invite.failed_title', body: 'invite.failed_body', tone: 'refusal' },
} as const satisfies Record<InviteScreen, InviteCopy>;

/** A dated footnote under the copy: which key to render, and the instant to format. */
export type InviteDetail = { readonly key: string; readonly when: string };

/**
 * The date behind an outcome, or `null` when it has none. Null is a real answer
 * twice over: most outcomes carry no instant at all, and the two 410s carry one
 * only from an API that ships the `reason` extension — so the line renders when
 * it exists rather than printing `Invalid Date`.
 */
export function inviteDetail(outcome: InviteOutcome): InviteDetail | null {
  switch (outcome.kind) {
    case 'expired':
      return outcome.at === null ? null : { key: 'invite.when_expired', when: outcome.at };
    case 'revoked':
      return outcome.at === null ? null : { key: 'invite.when_revoked', when: outcome.at };
    case 'already_accepted':
      return outcome.acceptedAt === null
        ? null
        : { key: 'invite.when_accepted', when: outcome.acceptedAt };
    default:
      return null;
  }
}
